// =============================================================================
// SIREPPAR - Hook: las alianzas del partido del usuario externo
// =============================================================================
// Espeja a `useReconocimientoApoderado`: centraliza en un solo lugar el gating de
// la navegación y de los carteles del inicio, para que MainLayout y DashboardPage
// no dupliquen la lógica de "en qué estado está la alianza".
//
// Devuelve un ARRAY: un partido puede integrar más de una alianza (convocatorias
// distintas), y en ese caso el inicio muestra un banner por cada una.
//
// La navegación se acota SÓLO cuando la agrupación activa es la alianza. Son dos
// preguntas distintas y conviene no confundirlas:
//   - `tieneAcceso` (del backend): ¿puede entrar a esa alianza? Decide si el banner
//     del inicio le ofrece el botón para cambiar de contexto.
//   - agrupación activa: ¿con cuál está trabajando AHORA? Decide la navegación.
// Un apoderado con membresía sobre una alianza sigue administrando su propio
// partido con el menú completo hasta que cambia de contexto.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { alianzaGestionService, type AlianzaDelPartido } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];

// Estados del expediente en los que la alianza está EN CURSO: el trámite está
// abierto y todavía no hay acta del TEP que lo resuelva.
// EN_TEP entra acá porque es la etapa MÁS LARGA del circuito: sin él, la alianza
// elevada al Tribunal dejaba de contar como "en trámite" justo cuando más tiempo
// pasa esperando, y al apoderado se le devolvía la navegación completa como si el
// expediente ya estuviera resuelto.
// NO_PRESENTADO se conserva sólo para los expedientes viejos que quedaron ahí: en
// alianzas ese circuito ya no existe y no hay camino nuevo que lleve a ese estado.
const ESTADOS_EN_TRAMITE = [
  'PRESENTADO', 'EN_ADMISIBILIDAD', 'EN_ANALISIS', 'OBSERVADO',
  'EN_SECRETARIO', 'PENDIENTE_RESOLUCION', 'EN_TEP', 'NO_PRESENTADO',
];

export function esAlianzaEnTramite(alianza: AlianzaDelPartido): boolean {
  return ESTADOS_EN_TRAMITE.includes(alianza.estadoExpediente);
}

export function useAlianzaPartido() {
  const { user } = useAuth();
  const esExterno = !!user?.partidoId && !ROLES_INTERNOS.includes(user?.rol || '');

  const { data, isLoading } = useQuery({
    queryKey: ['mis-alianzas'],
    queryFn: alianzaGestionService.misAlianzas,
    enabled: esExterno,
  });

  const alianzas = data ?? [];
  // La alianza que el usuario está ADMINISTRANDO AHORA: la que coincide con su
  // agrupación activa (`user.partidoId` ya viene sobreescrito con el contexto de la
  // sesión, ver middleware/auth).
  //
  // Antes era "la primera a la que tenga acceso", y eso mezclaba dos cosas
  // distintas: con una membresía activa sobre una alianza, el menú se le acotaba a
  // Inicio/Expedientes/Usuarios AUNQUE estuviera trabajando su propio partido, y
  // perdía Afiliados, Financiamiento y el resto. Tener acceso no es estar usándola.
  const alianzaPropia = alianzas.find(a => a.alianzaId === user?.partidoId) ?? null;
  // Las demás: generan el banner del inicio, con o sin botón para entrar según
  // `tieneAcceso`. No tocan la navegación.
  const alianzasQueIntegra = alianzas.filter(a => a.alianzaId !== user?.partidoId);

  return {
    isLoading,
    alianzas,
    alianzaPropia,
    alianzasQueIntegra,
    hayAlianzas: alianzas.length > 0,
    // Gestiona una alianza con el trámite abierto: habilita la navegación acotada.
    gestionaAlianzaEnTramite: !!alianzaPropia && esAlianzaEnTramite(alianzaPropia),
    // Gestiona una alianza, en cualquier estado (incluida ya resuelta).
    gestionaAlianza: !!alianzaPropia,
    // La alianza quedó oficializada por el TEP: recién ahí está habilitada para
    // oficializar listas, y se le abre Candidaturas.
    alianzaPropiaVigente: alianzaPropia?.estadoExpediente === 'VIGENTE'
      || alianzaPropia?.estadoAlianza === 'VIGENTE',
  };
}
